import { ArrowBack, AttachMoney, FilterList, Payments } from '@mui/icons-material'
import { Box, Button, Drawer, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import dayjs from 'dayjs'
import { t } from 'i18next'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { splitName } from '../../store/utils/usersUtils'
import DatePickerWithArrows from '../../components/DatePickerWithArrows/DatePickerWithArrows'
import FilterProjectCostsMenu, {
  initialProjectCostsFilters,
  ProjectCostsFilterType,
} from '../../components/FilterMenu/Projects/FilterProjectCostsMenu'
import Searchbar from '../../components/Searchbar/Searchbar'
import ProjectCostsTable from '../../components/Table/ProjectCosts/ProjectCostsTable'
import defaultProps from '../../config/defaultProps'
import { useGetCostsQuery, useGetSumQuery } from '../../store/redux/reducers/costSlice'
import { RequestProps } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import { SquareButton } from '../../styles/ButtonStyles'
import { StyledContainerRowBetween } from '../../styles/ContainerStyles'

type ProjectCostsDrawerProps = {
  resize: boolean
  fullWidth: boolean
  projectId: string
  isEditMode: boolean
  isNotLeaderOfProject: boolean
  selectedDate: dayjs.Dayjs
  handleDateChange: (e: dayjs.Dayjs | null) => void
}

const ProjectCostsDrawer = ({
  resize,
  fullWidth,
  projectId,
  isEditMode,
  isNotLeaderOfProject,
  selectedDate,
  handleDateChange,
}: ProjectCostsDrawerProps) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleOpen = () => setOpen(true)

  const handleClose = () => setOpen(false)

  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isDrawerFullWidth = useMediaQuery(theme.breakpoints.down(850))
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))
  const [searchInput, setSearchInput] = useState('')
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState<ProjectCostsFilterType>(initialProjectCostsFilters)
  const [requestProps, setRequestProps] = useState<RequestProps>({
    ...defaultProps,
    'project.id': projectId,
    yearMonth: dayjs(selectedDate).format(DateFormat.YearMonth),
  })

  const { data: costsResponse, isLoading } = useGetCostsQuery(requestProps)
  const { data: totalCost } = useGetSumQuery(requestProps)

  const buildFilterParams = useCallback((filters: ProjectCostsFilterType, search: string): Record<string, string> => {
    const params: Record<string, string> = {}

    if (search) {
      const { firstName, lastName } = splitName(search)
      if (lastName) {
        params['author.firstName'] = firstName
        params['author.lastName'] = lastName
      } else {
        params['query:author.firstName,author.lastName,description,principal'] = firstName
      }
    }

    if (filters.employees.length > 0) {
      params['author.id'] = filters.employees.map(emp => emp.id).join(',')
    }

    if (filters.costTypes.length > 0) {
      params.type = filters.costTypes.join(',')
    }

    return params
  }, [])

  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((search: string) => {
        const filterParams = buildFilterParams(selectedFilters, search)

        setRequestProps({
          ...defaultProps,
          'project.id': projectId,
          yearMonth: dayjs(selectedDate).format(DateFormat.YearMonth),
          ...filterParams,
          page: 0,
        })
      }, 300),
    [buildFilterParams, projectId, selectedDate, selectedFilters]
  )

  useEffect(() => {
    debouncedUpdateSearch(searchInput)
    return () => {
      debouncedUpdateSearch.cancel()
    }
  }, [searchInput, debouncedUpdateSearch])

  useEffect(() => {
    debouncedUpdateSearch.cancel()
    const filterParams = buildFilterParams(selectedFilters, searchInput)
    setRequestProps({
      ...defaultProps,
      'project.id': projectId,
      yearMonth: dayjs(selectedDate).format(DateFormat.YearMonth),
      ...filterParams,
      page: 0,
    })
  }, [selectedFilters, projectId, selectedDate, buildFilterParams, debouncedUpdateSearch, searchInput])

  useEffect(() => {
    if ((costsResponse?.content?.length ?? 0) === 0) {
      setIsFilterMenuOpen(false)
    }
  }, [costsResponse])

  return (
    <>
      {resize ? (
        <Tooltip title={t('page.projects.detailsView.projectCosts')} placement="bottom">
          <SquareButton onClick={handleOpen} color="secondary" sx={{ display: isNotLeaderOfProject ? 'none' : 'auto' }}>
            <Payments />
          </SquareButton>
        </Tooltip>
      ) : (
        <Button
          variant={'contained'}
          startIcon={<AttachMoney />}
          color="secondary"
          size="small"
          onClick={handleOpen}
          fullWidth={fullWidth}
          sx={{ display: isNotLeaderOfProject ? 'none' : 'auto' }}
        >
          {t('page.projects.detailsView.projectCosts')}
        </Button>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: 'background.main',
            width: isDrawerFullWidth ? '100%' : 850,
            height: '100%',
          },
          elevation: 0,
        }}
      >
        <Stack px={isDownSm ? 3 : 6} pt={3} pb={3} sx={{ height: '100%' }}>
          <Button
            onClick={handleClose}
            variant="text"
            color="secondary"
            startIcon={<ArrowBack />}
            size="small"
            sx={{ position: 'absolute', ml: -3 }}
          >
            {t('components.infoSidebar.return')}
          </Button>
          <Stack sx={{ pt: 6, gap: 2, flex: 1, minHeight: 0 }}>
            <StyledContainerRowBetween>
              <Typography variant="h5" color="text.mediumEmphasis">
                {t('page.projects.detailsView.projectCosts')}
              </Typography>
              <Button
                variant="contained"
                disableElevation
                startIcon={<FilterList />}
                size="small"
                color="secondary"
                className={isFilterMenuOpen ? 'selected' : ''}
                onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
              >
                {t('page.usersProfile.filterTags')}
              </Button>
            </StyledContainerRowBetween>

            {isDownLg && (
              <Collapse in={isFilterMenuOpen} orientation="vertical">
                <FilterProjectCostsMenu setFilteringOptions={setSelectedFilters} costs={costsResponse?.content || []} />
              </Collapse>
            )}

            <Stack
              direction="row"
              sx={{
                gap: isFilterMenuOpen ? 2 : 0,
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: isDownLg ? undefined : 'hidden',
              }}
            >
              <Stack sx={{ gap: 2, flex: 1, minHeight: 0, minWidth: 0, overflow: isDownLg ? undefined : 'auto' }}>
                <Stack
                  direction={isDrawerFullWidth ? 'column' : 'row'}
                  spacing={2}
                  alignItems={isDrawerFullWidth ? 'center' : 'flex-start'}
                  justifyContent={'space-between'}
                >
                  <Box width="100%" maxWidth={400}>
                    <Searchbar
                      placeholder={t('components.placeholder.search')}
                      handleChange={(value: string) => setSearchInput(value)}
                    />
                  </Box>

                  <DatePickerWithArrows
                    disabled={isEditMode || isNotLeaderOfProject}
                    onChange={handleDateChange}
                    value={dayjs(selectedDate)}
                    view="month"
                    views={['month', 'year']}
                    containerSxProps={{ ml: isDownSm ? undefined : 'auto', minWidth: 240 }}
                    sx={{ width: 'fit-content' }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        inputProps: { 'aria-label': 'Select a month and year' },
                      },
                    }}
                  />
                </Stack>

                <ProjectCostsTable
                  costs={costsResponse?.content || []}
                  isLoading={isLoading}
                  totalElements={costsResponse?.totalElements || 0}
                  totalCost={totalCost || 0}
                  setRequestProps={setRequestProps}
                />
              </Stack>

              {!isDownLg && (
                <Collapse in={isFilterMenuOpen} orientation="horizontal">
                  <FilterProjectCostsMenu
                    setFilteringOptions={setSelectedFilters}
                    costs={costsResponse?.content || []}
                  />
                </Collapse>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}

export default ProjectCostsDrawer
